import React, { useState } from 'react'
import { FaLinkedin } from 'react-icons/fa'
import 'react-loading-skeleton/dist/skeleton.css'
import aiImg from '../../../assets/images/ai-avatar.svg'
import Table from '../../molecules/Table'
import TableLayout from '../../molecules/TableLayout'
import { StyledProspectsResult } from './NewOnboarding.styles'

const COLUMN_NAMES = ['Name', 'Headline', 'Social Media URLs']
const DEFAULT_TOTAL_ROWS = 10

const AVATAR_COLORS = ['#6C5CE7', '#0984E3', '#00B894', '#E17055', '#E84393', '#00CEC9', '#E1A100', '#636E72']

const getInitials = (name = '') => {
    const parts = name.trim().split(/\s+/).filter(Boolean)
    if (!parts.length) return '?'
    if (parts.length === 1) return parts[0].slice(0, 2)
    return parts[0][0] + parts[parts.length - 1][0]
}

const colorForName = (name = '') =>
    AVATAR_COLORS[Array.from(name).reduce((acc, ch) => (acc * 31 + ch.charCodeAt(0)) >>> 0, 0) % AVATAR_COLORS.length]

const ProspectAvatar = ({ src, name }) => {
    const [errored, setErrored] = useState(false)
    if (src && !errored) {
        return <img src={src} alt={name} referrerPolicy='no-referrer' onError={() => setErrored(true)} />
    }
    return (
        <span className='initials-avatar' style={{ background: colorForName(name) }} aria-hidden='true'>
            {getInitials(name)}
        </span>
    )
}

const NameCell = ({ prospect }) => (
    <div className='name-cell'>
        <span className='avatar'>
            <ProspectAvatar src={prospect.avatar} name={prospect.name} />
        </span>
        <span className='name'>{prospect.name}</span>
    </div>
)

const SocialCell = ({ prospect }) => (
    <div className='social-cell'>
        {prospect.linkedinUrl ? (
            <a
                className='social-link'
                href={prospect.linkedinUrl}
                target='_blank'
                rel='noopener noreferrer'
                title={`View ${prospect.name} on LinkedIn`}
                aria-label={`View ${prospect.name} on LinkedIn`}
            >
                <FaLinkedin size={22} />
            </a>
        ) : (
            <span className='social-empty'>—</span>
        )}
    </div>
)

const buildRealRowCells = (prospect) => [
    <NameCell prospect={prospect} key='name' />,
    <div className='headline-cell' key='headline'>
        <span className='headline'>{prospect.headline || '—'}</span>
    </div>,
    <SocialCell prospect={prospect} key='social' />,
]

const ProspectsTable = ({ rowsData, page, totalRows, loading }) => (
    <div className='table-card'>
        <TableLayout
            noStyle
            noPagination
            currentPage={page}
            pageSize={totalRows}
            totalCount={totalRows}
        >
            <Table
                columnNames={COLUMN_NAMES}
                rowsData={rowsData}
                loading={loading}
                loadingSkeletonLength={totalRows}
                noPaddingBottom='0'
                noNewStyle
                responsive={false}
            />
        </TableLayout>
    </div>
)

const Pagination = ({ page, totalPages, hasMore, loading, onPageChange }) => (
    <div className='lead-pagination'>
        <button
            type='button'
            className='page-btn'
            disabled={page <= 1 || loading}
            onClick={() => onPageChange?.(page - 1)}
        >
            ← Prev
        </button>
        <span className='page-indicator'>Page {page}/{totalPages}</span>
        <button
            type='button'
            className='page-btn'
            disabled={!hasMore || loading}
            onClick={() => onPageChange?.(page + 1)}
        >
            Next →
        </button>
    </div>
)

const OptionButtons = ({ options, onSelectOption }) => (
    <div className='btn-holder'>
        {options?.map(opt => (
            <button
                key={opt.id}
                type='button'
                onClick={() => onSelectOption?.(opt)}
            >
                {opt?.label}
            </button>
        ))}
    </div>
)

const ProspectsResult = ({
    message = '',
    prospects = [],
    totalRows = DEFAULT_TOTAL_ROWS,
    total = 0,
    loading = false,
    paginated = false,
    page = 1,
    hasMore = false,
    onPageChange,
    options = [],
    onSelectOption,
}) => {
    const rowsData = (prospects || []).map((p) => ({
        id: p.id,
        data: p,
        cells: buildRealRowCells(p),
    }))

    const totalPages = Math.max(1, Math.ceil((total || 0) / (totalRows || 1)))

    return (
        <StyledProspectsResult>
            <div className='intro-row'>
                <div className='img-holder'>
                    <img src={aiImg} alt='aiImg' />
                </div>
                <div className='content'>
                    {message
                        .split('\n\n')
                        .filter(Boolean)
                        .map((paragraph, index) => (
                            <span className='intro-text' key={index}>{paragraph}</span>
                        ))}
                    <ProspectsTable rowsData={rowsData} page={page} totalRows={totalRows} loading={loading} />

                    {paginated && (
                        <Pagination page={page} totalPages={totalPages} hasMore={hasMore} loading={loading} onPageChange={onPageChange} />
                    )}

                    {options?.length > 0 && (
                        <OptionButtons options={options} onSelectOption={onSelectOption} />
                    )}
                </div>
            </div>

        </StyledProspectsResult>
    )
}

export default ProspectsResult
